import { CheckIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { useBlock, useBlockValidation } from "./block-store";
import { useIsReadonly } from "./readonly-context";

import { BlockProps } from ".";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/LoadingSpinner";

interface SaveButtonProps {
  onSave?: BlockProps["onSave"];
  requireValidation?: boolean;
}

const SaveButton = ({ onSave, requireValidation = true }: SaveButtonProps) => {
  const block = useBlock();
  const { triggerSaveValidation, errors } = useBlockValidation();
  const isReadonly = useIsReadonly();
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  if (isReadonly) {
    return null;
  }

  const handleManualSave = async () => {
    const validationResult = triggerSaveValidation();

    if (!requireValidation || validationResult.isValid) {
      try {
        setIsSaving(true);
        await onSave?.(block.payload);
        block.markAsSaved();

        // Show "Saved" feedback for 2 seconds
        setShowSaved(true);
        setTimeout(() => setShowSaved(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }
  };

  const hasVisibleErrors = Object.keys(errors).length > 0;
  const { hasChanges } = block;
  const isDisabled = hasVisibleErrors || isSaving || !hasChanges;

  // Button content
  let buttonContent: React.ReactNode = "Save";
  if (isSaving) {
    buttonContent = (
      <>
        <LoadingSpinner size="4" className="mr-2" />
        Saving
      </>
    );
  } else if (showSaved && !hasChanges) {
    buttonContent = (
      <>
        <CheckIcon className="mr-2 h-4 w-4" />
        Saved
      </>
    );
  } else if (!hasChanges) {
    buttonContent = "No changes";
  }

  const button = (
    <Button
      onClick={handleManualSave}
      disabled={isDisabled}
      variant={showSaved && !hasChanges ? "secondary" : "default"}
      className="w-full"
    >
      {buttonContent}
    </Button>
  );

  // Add tooltip for disabled states
  if (hasVisibleErrors && !isSaving) {
    return (
      <BasicTooltip content="Please correct the errors above">
        <span className="inline-block w-full">{button}</span>
      </BasicTooltip>
    );
  }

  if (!hasChanges && !showSaved && !isSaving) {
    return (
      <BasicTooltip content="Make changes to enable saving">
        <span className="inline-block w-full">{button}</span>
      </BasicTooltip>
    );
  }

  return button;
};

export default observer(SaveButton);
